import React, { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Trash2, Save, RotateCcw } from "lucide-react";

export default function AccessControl() {
  return (
    <div data-testid="access-control-module">
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-slate-900">Access Control</h2>
        <p className="text-sm text-slate-500">Manage collaboration shares, per-user overrides, invitations and access logs.</p>
      </div>
      <Tabs defaultValue="shares">
        <TabsList className="mb-4 flex-wrap h-auto">
          <TabsTrigger value="shares" data-testid="ac-tab-shares">Shares</TabsTrigger>
          <TabsTrigger value="overrides" data-testid="ac-tab-overrides">User Overrides</TabsTrigger>
          <TabsTrigger value="invitations" data-testid="ac-tab-invitations">Invitations</TabsTrigger>
          <TabsTrigger value="logs" data-testid="ac-tab-logs">Access Logs</TabsTrigger>
        </TabsList>
        <TabsContent value="shares"><SharesTab /></TabsContent>
        <TabsContent value="overrides"><OverridesTab /></TabsContent>
        <TabsContent value="invitations"><InvitationsTab /></TabsContent>
        <TabsContent value="logs"><LogsTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function SharesTab() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("active");
  const load = useCallback(() => api.get(`/access/shares?status=${status}`).then((r) => setRows(r.data)).catch(() => {}), [status]);
  useEffect(() => { load(); }, [load]);
  const revoke = async (id) => { await api.post(`/shares/${id}/revoke`); toast.success("Revoked"); load(); };
  return (
    <div className="bg-white border rounded-xl p-4">
      <div className="flex items-center gap-2 mb-3">
        <Label className="text-sm">Status</Label>
        <Select value={status} onValueChange={setStatus}><SelectTrigger className="w-40 h-9" data-testid="shares-status"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="all">All</SelectItem><SelectItem value="revoked">Revoked</SelectItem></SelectContent></Select>
      </div>
      <div className="overflow-x-auto"><table className="w-full text-sm">
        <thead className="text-left text-xs text-slate-500 border-b"><tr><th className="py-2">Type</th><th>Shared With</th><th>Permission</th><th>Status</th><th>Expires</th><th></th></tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={6} className="py-4 text-slate-400">No shares.</td></tr>}
          {rows.map((s) => (
            <tr key={s.id} data-testid={`ac-share-${s.id}`} className="border-b last:border-0">
              <td className="py-2 capitalize">{s.entity_type}</td>
              <td>{s.shared_with_name}</td>
              <td className="capitalize">{s.permission}</td>
              <td><span className={s.status === "active" ? "text-emerald-600" : "text-red-500"}>{s.status}</span></td>
              <td className="text-slate-400">{s.expires_at ? new Date(s.expires_at).toLocaleDateString() : "—"}</td>
              <td className="text-right">{s.status === "active" && <button onClick={() => revoke(s.id)} data-testid={`ac-revoke-${s.id}`} className="p-1.5 rounded text-slate-400 hover:text-red-600 hover:bg-red-50"><Trash2 size={14} /></button>}</td>
            </tr>
          ))}
        </tbody>
      </table></div>
    </div>
  );
}

function OverridesTab() {
  const [users, setUsers] = useState([]);
  const [uid, setUid] = useState("");
  const [cfg, setCfg] = useState(null);
  useEffect(() => { api.get("/users").then((r) => setUsers(r.data)).catch(() => setUsers([])); }, []);
  useEffect(() => { if (uid) api.get(`/access/overrides/${uid}`).then((r) => setCfg(r.data)); else setCfg(null); }, [uid]);
  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const save = async () => { await api.put(`/access/overrides/${uid}`, cfg); toast.success("Override saved"); };
  const reset = async () => { await api.delete(`/access/overrides/${uid}`); toast.success("Reset to role default"); setCfg({ user_id: uid, all_projects: false, extra_project_ids: [], all_tasks: false, workforce: null, can_share: false, can_invite: false, expires_at: null }); };
  return (
    <div className="bg-white border rounded-xl p-5 max-w-xl space-y-4">
      <div className="space-y-1">
        <Label>User</Label>
        <Select value={uid} onValueChange={setUid}><SelectTrigger data-testid="ov-user"><SelectValue placeholder="Select user…" /></SelectTrigger>
          <SelectContent>{users.filter((u) => u.role !== "master_admin").map((u) => <SelectItem key={u.id} value={u.id}>{u.full_name} ({u.role})</SelectItem>)}</SelectContent></Select>
      </div>
      {cfg && <div className="space-y-3">
        <ToggleRow label="Grant access to ALL projects" checked={cfg.all_projects} onChange={(v) => set("all_projects", v)} testid="ov-all-projects" />
        <ToggleRow label="Grant access to ALL tasks" checked={cfg.all_tasks} onChange={(v) => set("all_tasks", v)} testid="ov-all-tasks" />
        <ToggleRow label="Can share records" checked={cfg.can_share} onChange={(v) => set("can_share", v)} testid="ov-can-share" />
        <ToggleRow label="Can invite users" checked={cfg.can_invite} onChange={(v) => set("can_invite", v)} testid="ov-can-invite" />
        <div className="flex items-center justify-between"><Label className="text-sm">Workforce visibility</Label>
          <Select value={cfg.workforce || "default"} onValueChange={(v) => set("workforce", v === "default" ? null : v)}><SelectTrigger className="w-40 h-9" data-testid="ov-workforce"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="default">Role default</SelectItem><SelectItem value="self">Self</SelectItem><SelectItem value="team">Own team</SelectItem><SelectItem value="all">All</SelectItem></SelectContent></Select></div>
        <div className="space-y-1"><Label className="text-sm">Temporary access expiry (optional)</Label>
          <Input type="datetime-local" value={cfg.expires_at ? cfg.expires_at.slice(0, 16) : ""} onChange={(e) => set("expires_at", e.target.value || null)} data-testid="ov-expiry" /></div>
        <div className="flex gap-2 pt-1">
          <Button onClick={save} data-testid="ov-save"><Save size={14} className="mr-1.5" />Save Override</Button>
          <Button variant="outline" onClick={reset} data-testid="ov-reset"><RotateCcw size={14} className="mr-1.5" />Reset to Role Default</Button>
        </div>
      </div>}
    </div>
  );
}

function ToggleRow({ label, checked, onChange, testid }) {
  return <div className="flex items-center justify-between"><span className="text-sm text-slate-600">{label}</span><Switch checked={!!checked} onCheckedChange={onChange} data-testid={testid} /></div>;
}

function InvitationsTab() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ email: "", role: "employee" });
  const load = useCallback(() => api.get("/access/invitations").then((r) => setRows(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const create = async () => { if (!form.email) return; try { await api.post("/access/invitations", form); toast.success("Invitation created"); setForm({ email: "", role: "employee" }); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const revoke = async (id) => { await api.post(`/access/invitations/${id}/revoke`); load(); };
  return (
    <div className="bg-white border rounded-xl p-5 space-y-4">
      <div className="flex flex-wrap gap-2 items-end">
        <div className="space-y-1"><Label className="text-xs">Email</Label><Input className="w-64" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="inv-email" placeholder="person@company.com" /></div>
        <div className="space-y-1"><Label className="text-xs">Role</Label>
          <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}><SelectTrigger className="w-36 h-10" data-testid="inv-role"><SelectValue /></SelectTrigger>
            <SelectContent>{["employee", "manager", "viewer", "admin"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent></Select></div>
        <Button onClick={create} data-testid="inv-create">Send Invitation</Button>
      </div>
      <div className="overflow-x-auto"><table className="w-full text-sm">
        <thead className="text-left text-xs text-slate-500 border-b"><tr><th className="py-2">Email</th><th>Role</th><th>Status</th><th>Invited By</th><th></th></tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={5} className="py-4 text-slate-400">No invitations.</td></tr>}
          {rows.map((i) => (
            <tr key={i.id} data-testid={`inv-${i.id}`} className="border-b last:border-0">
              <td className="py-2">{i.email}</td><td className="capitalize">{i.role}</td>
              <td><span className={i.status === "pending" ? "text-amber-600" : "text-slate-400"}>{i.status}</span></td>
              <td className="text-slate-400">{i.invited_by}</td>
              <td className="text-right">{i.status === "pending" && <button onClick={() => revoke(i.id)} data-testid={`inv-revoke-${i.id}`} className="p-1.5 rounded text-slate-400 hover:text-red-600 hover:bg-red-50"><Trash2 size={14} /></button>}</td>
            </tr>
          ))}
        </tbody>
      </table></div>
    </div>
  );
}

function LogsTab() {
  const [rows, setRows] = useState([]);
  const [result, setResult] = useState("all");
  const load = useCallback(() => { const qs = result === "all" ? "" : `?result=${result}`; api.get(`/access/logs${qs}`).then((r) => setRows(r.data)).catch(() => {}); }, [result]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="bg-white border rounded-xl p-4">
      <div className="flex items-center gap-2 mb-3"><Label className="text-sm">Result</Label>
        <Select value={result} onValueChange={setResult}><SelectTrigger className="w-40 h-9" data-testid="logs-result"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="denied">Denied</SelectItem><SelectItem value="allowed">Allowed</SelectItem></SelectContent></Select></div>
      <div className="overflow-x-auto"><table className="w-full text-sm">
        <thead className="text-left text-xs text-slate-500 border-b"><tr><th className="py-2">User</th><th>Entity</th><th>Action</th><th>Result</th><th>IP</th><th>Time</th></tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={6} className="py-4 text-slate-400">No access logs.</td></tr>}
          {rows.map((l) => (
            <tr key={l.id} className="border-b last:border-0">
              <td className="py-2">{l.user_name}</td><td className="capitalize">{l.entity_type} <span className="text-slate-400 text-xs">{(l.entity_id || "").slice(0, 8)}</span></td>
              <td>{l.action}</td><td><span className={l.result === "allowed" ? "text-emerald-600" : "text-red-500"}>{l.result}</span></td>
              <td className="text-slate-400">{l.ip || "—"}</td><td className="text-slate-400 text-xs">{new Date(l.timestamp).toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table></div>
    </div>
  );
}
